import {
	ActionIcon,
	AppShell,
	Burger,
	Group,
	Menu,
	NavLink,
	Text,
	Tooltip,
	UnstyledButton,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
	IconBed,
	IconBuilding,
	IconChartBar,
	IconChevronLeft,
	IconChevronRight,
	IconClipboardList,
	IconDiscount2,
	IconFileDescription,
	IconGift,
	IconHistory,
	IconLogout,
	IconSettings,
	IconUserCircle,
	IconUsers,
} from "@tabler/icons-react";
import { type ReactNode, useCallback, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate } from "react-router";

import { APP_NAV, type AppNavItem, navVisible } from "@/app/routes";
import { SeasonFx } from "@/app/season-header-fx";
import { useMotion } from "@/app/use-motion";
import { useSidebarDrag } from "@/app/use-sidebar-drag";
import { logoutStaff } from "@/features/auth/api/auth-staff";
import { clearSession, getRefreshToken } from "@/features/auth/lib/auth-store";
import { useAuth } from "@/features/auth/lib/use-auth";
import { useSeason } from "@/features/content/use-season";
import { PlayBrand } from "@/shared/ui/play-brand";
import { SeasonBadge } from "@/shared/ui/season-badge";

const NAV_WIDTH = { expanded: 260, mini: 76 } as const;
const NAV_MINI_KEY = "play-admin.nav-mini";

function readNavMini() {
	try {
		return window.localStorage.getItem(NAV_MINI_KEY) === "1";
	} catch {
		return false;
	}
}

function persistNavMini(mini: boolean) {
	try {
		window.localStorage.setItem(NAV_MINI_KEY, mini ? "1" : "0");
	} catch {
		/* ignore quota / private mode */
	}
}

function writeNavbarWidth(mini: boolean) {
	document.documentElement.style.setProperty(
		"--play-navbar-width",
		`${mini ? NAV_WIDTH.mini : NAV_WIDTH.expanded}px`,
	);
}

const NAV_ICONS: Record<string, ReactNode> = {
	"/applications": <IconFileDescription size={18} />,
	"/users": <IconUsers size={18} />,
	"/discounts": <IconDiscount2 size={18} />,
	"/partners": <IconBuilding size={18} />,
	"/referral": <IconGift size={18} />,
	"/referral/settings": <IconSettings size={18} />,
	"/referral/stats": <IconChartBar size={18} />,
	"/referral/redemptions": <IconClipboardList size={18} />,
	"/hotel-promos": <IconBed size={18} />,
	"/audit-log": <IconHistory size={18} />,
	"/account": <IconUserCircle size={18} />,
};

export function AdminAppShell() {
	const { t } = useTranslation();
	const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] =
		useDisclosure();
	const [desktopMini, setDesktopMini] = useState(readNavMini);
	const isDesktop = useMediaQuery("(min-width: 48em)", true, {
		getInitialValueInEffect: false,
	});
	const compact = Boolean(isDesktop && desktopMini);
	const { motion } = useMotion();
	const location = useLocation();
	const navigate = useNavigate();
	const { session, isPortalAdmin, canWorkRequests } = useAuth();
	const { season } = useSeason();

	const navItems = APP_NAV.filter((item) =>
		navVisible(item, { canWorkRequests, isPortalAdmin }),
	);

	const setMini = useCallback((mini: boolean) => {
		setDesktopMini(mini);
		persistNavMini(mini);
	}, []);
	const collapseNav = useCallback(() => setMini(true), [setMini]);
	const expandNav = useCallback(() => setMini(false), [setMini]);
	const { offset, bind: toggleDrag } = useSidebarDrag({
		enabled: Boolean(isDesktop),
		compact,
		onCollapse: collapseNav,
		onExpand: expandNav,
	});

	useLayoutEffect(() => {
		if (!isDesktop) {
			document.documentElement.style.removeProperty("--play-navbar-width");
			return;
		}
		writeNavbarWidth(desktopMini);
	}, [desktopMini, isDesktop]);

	const logout = () => {
		const refreshToken = getRefreshToken();
		clearSession();
		if (refreshToken) {
			void logoutStaff(refreshToken).catch(() => undefined);
		}
		navigate("/login", { replace: true });
	};

	return (
		<AppShell
			header={{ height: 56 }}
			navbar={{
				width: {
					base: "100vw",
					sm: compact ? NAV_WIDTH.mini : NAV_WIDTH.expanded,
				},
				breakpoint: "sm",
				collapsed: { mobile: !mobileOpened },
			}}
			padding={{ base: "sm", sm: "md" }}
			transitionDuration={motion ? 320 : 200}
			styles={{
				main: {
					background: "var(--play-canvas-bg)",
					minHeight: "100vh",
					minWidth: 0,
					overflowX: "hidden",
				},
			}}
		>
			<AppShell.Header px="md" className="play-app-header">
				<SeasonFx season={season} surface="header" />
				<Group
					h="100%"
					justify="space-between"
					wrap="nowrap"
					gap="sm"
					style={{ position: "relative", zIndex: 1 }}
				>
					<Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
						<Burger
							opened={mobileOpened}
							onClick={toggleMobile}
							hiddenFrom="sm"
							size="sm"
							aria-label={t("app.navMenu")}
						/>
						<PlayBrand size="sm" label={t("app.adminWordmark")} />
						<Tooltip label={t("app.seasonBadgeHint")}>
							<SeasonBadge season={season} visibleFrom="sm" />
						</Tooltip>
					</Group>
					<Menu shadow="md" width={220}>
						<Menu.Target>
							<UnstyledButton style={{ minWidth: 0 }}>
								<Text
									size="sm"
									fw={500}
									lineClamp={1}
									maw={{ base: 120, sm: 220 }}
								>
									{session?.fullName || session?.email || t("app.staff")}
								</Text>
							</UnstyledButton>
						</Menu.Target>
						<Menu.Dropdown>
							<Menu.Label>{session?.role ?? "STAFF"}</Menu.Label>
							<Menu.Item
								leftSection={<IconUserCircle size={16} />}
								onClick={() => {
									closeMobile();
									navigate("/account");
								}}
							>
								{t("nav.account")}
							</Menu.Item>
							<Menu.Divider />
							<Menu.Item
								leftSection={<IconLogout size={16} />}
								color="red"
								onClick={logout}
							>
								{t("app.logout")}
							</Menu.Item>
						</Menu.Dropdown>
					</Menu>
				</Group>
			</AppShell.Header>

			<AppShell.Navbar
				p={compact ? "xs" : "sm"}
				className="play-app-nav"
				data-nav-compact={compact || undefined}
			>
				<SeasonFx season={season} surface="nav" />
				<AppShell.Section
					grow
					style={{ overflowY: "auto", position: "relative", zIndex: 1 }}
				>
					{navItems.map((item) => (
						<AppNavLink
							key={item.path}
							item={item}
							pathname={location.pathname}
							compact={compact}
							onNavigate={closeMobile}
						/>
					))}
				</AppShell.Section>
			</AppShell.Navbar>

			<AppShell.Main>
				<div key={location.pathname} className="play-page">
					<Outlet />
				</div>
			</AppShell.Main>
			<div
				className="play-nav-toggle-wrap"
				data-compact={compact || undefined}
				style={{ transform: `translateX(${offset}px)` }}
			>
				<Tooltip
					label={desktopMini ? t("app.navExpand") : t("app.navCollapse")}
					position="right"
					offset={12}
					zIndex={300}
					openDelay={400}
					events={{ hover: true, focus: false, touch: false }}
				>
					<ActionIcon
						className="play-nav-toggle"
						variant="default"
						radius="xl"
						size={32}
						aria-label={desktopMini ? t("app.navExpand") : t("app.navCollapse")}
						{...toggleDrag}
						onClick={() => setMini(!desktopMini)}
					>
						{compact ? (
							<IconChevronRight size={16} />
						) : (
							<IconChevronLeft size={16} />
						)}
					</ActionIcon>
				</Tooltip>
			</div>
		</AppShell>
	);
}

function AppNavLink({
	item,
	pathname,
	compact,
	onNavigate,
}: {
	item: AppNavItem;
	pathname: string;
	compact: boolean;
	onNavigate: () => void;
}) {
	const { t } = useTranslation();
	const label = t(item.labelKey);
	const children = item.children ?? [];

	if (children.length > 0) {
		const childLinks = children.map((child) => (
			<AppNavLink
				key={child.path}
				item={child}
				pathname={pathname}
				compact={compact}
				onNavigate={onNavigate}
			/>
		));

		if (compact) {
			return (
				<div className="play-nav-group" data-compact>
					{childLinks}
				</div>
			);
		}

		return (
			<div className="play-nav-group">
				<NavLink
					label={label}
					leftSection={NAV_ICONS[item.path]}
					childrenOffset={12}
					defaultOpened
				>
					{childLinks}
				</NavLink>
			</div>
		);
	}

	const active = pathname === item.path || pathname.startsWith(`${item.path}/`);

	const link = (
		<NavLink
			component={Link}
			to={item.path}
			label={label}
			aria-label={label}
			leftSection={NAV_ICONS[item.path]}
			active={active}
			onClick={onNavigate}
			styles={
				compact
					? {
							root: {
								justifyContent: "center",
								paddingInline: 0,
							},
							body: { display: "none" },
							section: { marginInline: 0 },
						}
					: undefined
			}
		/>
	);

	if (!compact) {
		return link;
	}

	return (
		<Tooltip
			label={label}
			position="right"
			withArrow
			offset={22}
			openDelay={250}
			zIndex={500}
		>
			{link}
		</Tooltip>
	);
}
